import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Book, User, FileText, AlertCircle, CheckCircle, XCircle, Download, Eye, X } from 'lucide-react';

const StudentLessonsPortal = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pdfModal, setPdfModal] = useState({ isOpen: false, lessonId: null, pdfUrl: null });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/student/they`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 401) {
        setError('Please log in to view your lessons');
        return;
      }

      if (response.status === 403) {
        setError('Access denied. Student account required.');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch lessons');
      }

      const data = await response.json();
      setLessons(data || []);
      setError(null);
    } catch (err) {
      setError('Unable to load lessons. Please try again later.');
      console.error('Error fetching lessons:', err);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const fetchPdfFile = async (lessonId) => {
    try {
      setPdfLoading(true);
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/student/${lessonId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.status === 404) {
        showNotification('No lesson materials attached', 'warning');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch lesson materials');
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);
      
      setPdfModal({
        isOpen: true,
        lessonId: lessonId,
        pdfUrl: pdfUrl
      });
      showNotification('Materials loaded successfully', 'success');
    } catch (err) {
      showNotification('Unable to load materials. Please try again.', 'error');
      console.error('Error fetching PDF:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = async (lessonId, lessonSubject = 'lesson') => {
    try {
      const token = localStorage.getItem('auth_token');
      
      showNotification('Preparing download...', 'info');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/student/${lessonId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.status === 404) {
        showNotification('No lesson materials attached', 'warning');
        return;
      }

      if (!response.ok) {
        throw new Error('No lesson attached');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${lessonSubject}-lesson-${lessonId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showNotification('Download started successfully!', 'success');
    } catch (err) {
      showNotification('No lesson materials attached', 'warning');
      console.error('Error downloading PDF:', err);
    }
  };

  const closePdfModal = () => {
    if (pdfModal.pdfUrl) {
      URL.revokeObjectURL(pdfModal.pdfUrl);
    }
    setPdfModal({ isOpen: false, lessonId: null, pdfUrl: null });
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'canceled':
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'upcoming':
      default:
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'canceled':
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'upcoming':
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'No date set';
    try {
      const date = new Date(dateTimeString);
      return {
        date: date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        time: date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      };
    } catch (error) {
      return { date: dateTimeString, time: '' };
    }
  };

  const filteredLessons = lessons.filter(lesson => {
    if (filter === 'all') return true;
    return lesson.status?.toLowerCase() === filter;
  });

  const statusCounts = lessons.reduce((acc, lesson) => {
    const status = lesson.status?.toLowerCase() || 'upcoming';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const lessonsWithMaterials = lessons.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your lessons...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Lessons</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchLessons}
          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className={`rounded-lg shadow-lg p-4 flex items-center gap-3 ${
            notification.type === 'success' ? 'bg-green-100 border border-green-200 text-green-800' :
            notification.type === 'error' ? 'bg-red-100 border border-red-200 text-red-800' :
            notification.type === 'warning' ? 'bg-yellow-100 border border-yellow-200 text-yellow-800' :
            'bg-blue-100 border border-blue-200 text-blue-800'
          }`}>
            {notification.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0" />}
            {notification.type === 'error' && <XCircle className="w-5 h-5 flex-shrink-0" />}
            {notification.type === 'warning' && <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            {notification.type === 'info' && <FileText className="w-5 h-5 flex-shrink-0" />}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      

      {/* Enhanced Filter Buttons */}
      <div className="bg-white rounded-xl shadow-sm p-5 border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Your Lessons</h3>
            <p className="text-sm text-gray-600">Manage and access your tutoring sessions</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All', count: lessons.length },
              { key: 'upcoming', label: 'Upcoming', count: statusCounts.upcoming || 0 },
              { key: 'completed', label: 'Completed', count: statusCounts.completed || 0 },
              { key: 'canceled', label: 'Canceled', count: statusCounts.canceled || statusCounts.cancelled || 0 }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  filter === key
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                }`}
              >
                <span>{label}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  filter === key ? 'bg-blue-500' : 'bg-gray-300'
                }`}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Lessons List */}
      {filteredLessons.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border">
          <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filter === 'all' ? 'No lessons found' : `No ${filter} lessons`}
          </h3>
          <p className="text-gray-600">
            {filter === 'all' 
              ? 'Your lessons will appear here once scheduled by your tutor.'
              : `You don't have any ${filter} lessons at the moment.`
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLessons.map((lesson) => {
            const { date, time } = formatDateTime(lesson.dateTime);
            
            return (
              <div key={lesson.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-all duration-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(lesson.status)} flex items-center gap-2`}>
                          {getStatusIcon(lesson.status)}
                          {lesson.status || 'Upcoming'}
                        </span>
                        <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Materials Available
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Book className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Subject</p>
                            <p className="font-semibold text-gray-900">{lesson.subject || 'Not specified'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Calendar className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Date</p>
                            <p className="font-semibold text-gray-900">{date}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <Clock className="w-4 h-4 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Time & Duration</p>
                            <p className="font-semibold text-gray-900">
                              {time} {lesson.duration ? `(${lesson.duration}min)` : ''}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <User className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Grade Level</p>
                            <p className="font-semibold text-gray-900">{lesson.gradeLevel || 'Not specified'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Enhanced PDF buttons */}
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-purple-600">
                            <FileText className="w-5 h-5" />
                            <span className="font-medium">Lesson Materials</span>
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => fetchPdfFile(lesson.id)}
                              disabled={pdfLoading}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm"
                            >
                              <Eye className="w-4 h-4" />
                              {pdfLoading ? 'Loading...' : 'View Materials'}
                            </button>
                            <button
                              onClick={() => downloadPdf(lesson.id, lesson.subject)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PDF Modal */}
      {pdfModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white rounded-xl shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Lesson Materials</h3>
              <button
                onClick={closePdfModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 p-6">
              {pdfModal.pdfUrl ? (
                <iframe
                  src={pdfModal.pdfUrl}
                  className="w-full h-full border-0 rounded-lg"
                  title="Lesson PDF"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading lesson materials...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLessonsPortal;